import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { InputComponent, ToothSide } from '@libs/ui-lib';
import { NgTemplateOutlet } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { PerioMeasurementFormGroup, PerioMeasurementSurface } from '../perio-config/perio-config.vm';
import { PerioActiveToothFormUserInputDirective } from '../../directives/perio-active-tooth-form-user-input/perio-active-tooth-form-user-input.directive';
import { PerioFormControlService } from '../../services/perio-form-control/perio-form-control.service';

type PerioNumberConfigColor = 'purple' | 'pink';

@Component({
  selector: 'lib-perio-number-config-measurement',
  imports: [ReactiveFormsModule, InputComponent, PerioActiveToothFormUserInputDirective, NgTemplateOutlet],
  templateUrl: './perio-number-config-measurement.component.html',
  styleUrl: './perio-number-config-measurement.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class PerioNumberConfigMeasurementComponent {
  private readonly formControlService = inject(PerioFormControlService);

  public formGroup = input.required<FormGroup<PerioMeasurementFormGroup<number | undefined>>>();
  public rowLabel = input.required<string>();
  public colorVariant = input.required<PerioNumberConfigColor>();
  public readonly = input.required<boolean>();
  public reversed = input.required<boolean>();
  public isDcm = input.required<boolean>();
  public side = input.required<ToothSide>();
  public activeCursor = input<PerioMeasurementSurface | null>();
  public fgDisabled = input<boolean>(false);
  public disabledRecession = input<boolean>(false);
  public showGradient = input<boolean>(false);

  protected isDistalActive = computed(() => {
    return !this.readonly() && this.activeCursor() === 'distal';
  });
  protected isCentralActive = computed(() => {
    return !this.readonly() && this.activeCursor() === 'central';
  });

  protected isMesialActive = computed(() => {
    return !this.readonly() && this.activeCursor() === 'mesial';
  });

  protected activeField = toSignal(this.formControlService.focusedField$);
  protected isNextStroke = computed(() => {
    return (
      (this.colorVariant() === 'purple' && this.activeField() === 'pd') || (this.colorVariant() === 'pink' && this.activeField() === 'rec')
    );
  });

  protected colorClasses: Record<PerioNumberConfigColor, string> = {
    pink: 'text-states-pink',
    purple: 'text-states-purple',
  };

  protected gradientStyles: Record<PerioNumberConfigColor, string> = {
    purple: 'linear-gradient(to right, rgba(151, 71, 255, 0.6), rgba(145, 69, 241, 0))',
    pink: 'linear-gradient(to right, rgba(232, 72, 159, 0.6), rgba(232, 72, 159, 0))',
  };
}
